import React from 'react';
import { motion } from 'framer-motion';
import { 
  FileText, 
  TrendingUp, 
  CheckCircle,
  Clock,
  AlertTriangle,
  BarChart3,
  PieChart,
  MapPin,
  Info,
  Package
} from 'lucide-react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  Area,
  AreaChart
} from 'recharts';

interface NFEModalProps {
  data: {
    total_nfes: number;
    nfe_analytics: {
      status_distribution: {
        authorized: number;
        pending: number;
        cancelled: number;
        rejected: number;
      };
      monthly_volume: Array<{
        month: string;
        volume: number;
        value: number;
      }>;
      nfe_by_state: Array<{
        state: string;
        volume: number;
        percentage: number;
      }>;
      top_senders: Array<{
        cnpj: string;
        name: string;
        volume: number;
        value: number;
      }>;
    };
  };
}

export const NFEModal: React.FC<NFEModalProps> = ({ data }) => {
  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('pt-BR').format(value);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const COLORS = ['#10b981', '#f59e0b', '#ef4444', '#6b7280'];

  return (
    <div className="space-y-6">
      {/* Status Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="p-6 rounded-xl shadow-lg border relative overflow-hidden"
          style={{
            backgroundColor: 'var(--bg-card)',
            borderColor: 'var(--border-primary)',
            boxShadow: 'var(--shadow-md)'
          }}
        >
          <div className="relative flex items-center justify-between mb-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 shadow-lg">
              <CheckCircle className="h-6 w-6 text-white" />
            </div>
            <div className="text-right">
              <p 
                className="text-sm font-medium"
                style={{ color: 'var(--text-secondary)' }}
              >
                Autorizadas
              </p>
            </div>
          </div>
          <div className="space-y-2">
            <p 
              className="text-2xl font-bold"
              style={{ color: 'var(--text-primary)' }}
            >
              {formatNumber(data.nfe_analytics.status_distribution.authorized)}
            </p>
            <p 
              className="text-xs"
              style={{ color: 'var(--text-tertiary)' }}
            >
              NFes aprovadas
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="p-6 rounded-xl shadow-lg border relative overflow-hidden"
          style={{
            backgroundColor: 'var(--bg-card)',
            borderColor: 'var(--border-primary)',
            boxShadow: 'var(--shadow-md)'
          }}
        >
          <div className="relative flex items-center justify-between mb-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-yellow-500 to-yellow-600 shadow-lg">
              <Clock className="h-6 w-6 text-white" />
            </div>
            <div className="text-right">
              <p 
                className="text-sm font-medium"
                style={{ color: 'var(--text-secondary)' }}
              >
                Pendentes
              </p>
            </div>
          </div>
          <div className="space-y-2">
            <p 
              className="text-2xl font-bold"
              style={{ color: 'var(--text-primary)' }}
            >
              {formatNumber(data.nfe_analytics.status_distribution.pending)}
            </p>
            <p 
              className="text-xs"
              style={{ color: 'var(--text-tertiary)' }}
            >
              NFes aguardando
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="p-6 rounded-xl shadow-lg border relative overflow-hidden"
          style={{
            backgroundColor: 'var(--bg-card)',
            borderColor: 'var(--border-primary)',
            boxShadow: 'var(--shadow-md)'
          }}
        >
          <div className="relative flex items-center justify-between mb-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-red-500 to-red-600 shadow-lg">
              <AlertTriangle className="h-6 w-6 text-white" />
            </div>
            <div className="text-right">
              <p 
                className="text-sm font-medium"
                style={{ color: 'var(--text-secondary)' }}
              >
                Canceladas
              </p>
            </div>
          </div>
          <div className="space-y-2">
            <p 
              className="text-2xl font-bold"
              style={{ color: 'var(--text-primary)' }}
            >
              {formatNumber(data.nfe_analytics.status_distribution.cancelled)}
            </p>
            <p 
              className="text-xs"
              style={{ color: 'var(--text-tertiary)' }}
            >
              NFes canceladas
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="p-6 rounded-xl shadow-lg border relative overflow-hidden"
          style={{
            backgroundColor: 'var(--bg-card)',
            borderColor: 'var(--border-primary)',
            boxShadow: 'var(--shadow-md)'
          }}
        >
          <div className="relative flex items-center justify-between mb-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-gray-500 to-gray-600 shadow-lg">
              <FileText className="h-6 w-6 text-white" />
            </div>
            <div className="text-right">
              <p 
                className="text-sm font-medium"
                style={{ color: 'var(--text-secondary)' }}
              >
                Rejeitadas
              </p>
            </div>
          </div>
          <div className="space-y-2">
            <p 
              className="text-2xl font-bold"
              style={{ color: 'var(--text-primary)' }}
            >
              {formatNumber(data.nfe_analytics.status_distribution.rejected)}
            </p>
            <p 
              className="text-xs"
              style={{ color: 'var(--text-tertiary)' }}
            >
              NFes rejeitadas
            </p>
          </div>
        </motion.div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Volume Trend */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="rounded-xl p-6 shadow border"
          style={{
            backgroundColor: 'var(--bg-card)',
            borderColor: 'var(--border-primary)'
          }}
        >
          <h3 
            className="text-xl font-semibold mb-6 flex items-center"
            style={{ color: 'var(--text-primary)' }}
          >
            <BarChart3 
              className="h-5 w-5 mr-2" 
              style={{ color: 'var(--primary)' }}
            />
            Volume e Valor Mensal
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data.nfe_analytics.monthly_volume}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip 
                contentStyle={{
                  backgroundColor: 'var(--bg-card)',
                  border: '1px solid var(--border-primary)',
                  borderRadius: '8px'
                }}
                formatter={(value: number, name: string) => [
                  name === 'volume' ? formatNumber(value) : formatCurrency(value),
                  name === 'volume' ? 'Volume' : 'Valor'
                ]}
              />
              <Bar dataKey="volume" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </motion.div>

        {/* NFE by State */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="rounded-xl p-6 shadow border"
          style={{
            backgroundColor: 'var(--bg-card)',
            borderColor: 'var(--border-primary)'
          }}
        >
          <h3 
            className="text-xl font-semibold mb-6 flex items-center"
            style={{ color: 'var(--text-primary)' }}
          >
            <PieChart 
              className="h-5 w-5 mr-2" 
              style={{ color: 'var(--primary)' }}
            />
            Distribuição por Estado
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <RechartsPieChart>
              <Pie
                data={data.nfe_analytics.nfe_by_state}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ state, percentage }) => `${state} (${percentage.toFixed(1)}%)`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="volume"
              >
                {data.nfe_analytics.nfe_by_state.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip 
                contentStyle={{
                  backgroundColor: 'var(--bg-card)',
                  border: '1px solid var(--border-primary)',
                  borderRadius: '8px'
                }}
                formatter={(value: number) => [formatNumber(value), 'Volume']}
              />
            </RechartsPieChart>
          </ResponsiveContainer>
        </motion.div>
      </div>

      {/* Top Senders */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
        className="rounded-xl p-6 shadow border"
          style={{
            backgroundColor: 'var(--bg-card)',
            borderColor: 'var(--border-primary)'
          }}
      >
        <h3 
          className="text-xl font-semibold mb-6 flex items-center"
          style={{ color: 'var(--text-primary)' }}
        >
          <FileText 
            className="h-5 w-5 mr-2" 
            style={{ color: 'var(--primary)' }}
          />
          Principais Remetentes
        </h3>
        <div className="space-y-4">
          {data.nfe_analytics.top_senders.map((sender, index) => (
            <div 
              key={index} 
              className="flex items-center justify-between p-4 rounded-lg"
              style={{
                backgroundColor: 'var(--bg-card)',
                border: '1px solid var(--border-primary)',
                boxShadow: 'var(--shadow-md)'
              }}
            >
              <div className="flex items-center space-x-3">
                <div 
                  className="h-8 w-8 rounded-full flex items-center justify-center"
                  style={{
                    backgroundColor: 'var(--bg-tertiary)',
                    opacity: 0.8
                  }}
                >
                  <span 
                    className="text-sm font-bold"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    {index + 1}
                  </span>
                </div>
                <div>
                  <h4 
                    className="font-semibold"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    {sender.name}
                  </h4>
                  <p 
                    className="text-sm"
                    style={{ color: 'var(--text-secondary)' }}
                  >
                    {sender.cnpj}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <div 
                  className="text-lg font-bold"
                  style={{ color: 'var(--text-primary)' }}
                >
                  {formatNumber(sender.volume)}
                </div>
                <div 
                  className="text-sm"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  {formatCurrency(sender.value)}
                </div>
              </div>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Data Source Info */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
        className="p-4 rounded-lg border relative overflow-hidden"
        style={{
          backgroundColor: 'var(--bg-card)',
          borderColor: 'var(--border-primary)',
          boxShadow: 'var(--shadow-md)'
        }}
      >
        <div className="relative">
          <div className="flex items-start space-x-3">
          <Info 
            className="h-5 w-5 mt-0.5 flex-shrink-0" 
            style={{ color: 'var(--primary)' }}
          />
          <div>
            <h4 
              className="font-semibold mb-1"
              style={{ color: 'var(--text-primary)' }}
            >
              Origem dos Dados
            </h4>
            <p 
              className="text-sm"
              style={{ color: 'var(--text-secondary)' }}
            >
              Os dados de NFes são coletados automaticamente do sistema de emissão fiscal, 
              incluindo informações sobre status, valores, remetentes e distribuição geográfica. 
              Sincronizados com a Receita Federal em tempo real.
            </p>
          </div>
        </div>
        </div>
      </motion.div>
    </div>
  );
};
